import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, Subscription, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { HistoryService } from '../../../services/history/history.service';
import { IoService } from '../../../services/io/io.service';
import { AuthorizationService } from '../../../services/authorization/authorization.service';
import { LocationService } from '../../../services/location/location.service';
import { ReportService } from '../../../services/report/report.service'
import { RfPipe } from 'src/app/pipes/rf/rf.pipe';


@Component({
  selector: 'app-detail-user-group',
  templateUrl: './detail-user-group.component.html',
  styleUrls: ['./detail-user-group.component.scss']
})
export class DetailUserGroupComponent implements OnInit {

  documentId$: Observable<any>;
  documentId;
  showBackButton;
  activeUserIsDeveloper;
  userGroup;
  targetUserGroupAuthorizations;
  allPermissions;
  // Permissions grouped for display: User Groups, General, Views, Transfers,
  // Ledger Actions, Reports — derived from each permission's class tag.
  sections: { name: string, permissions: any[] }[] = [];
  hoverPermission: any = null;
  hoverCol = -1;

  delay = 500;


  constructor(
    private locationR: Location,
    private route: ActivatedRoute,
    private router: Router,
    private historyService: HistoryService,
    private authorizationService: AuthorizationService,
    private locationService: LocationService,
    private ioService: IoService,
    private reportService: ReportService,
    private rf: RfPipe,
  ) { }

  ngOnInit(): void {
    this.documentId$ = this.route.paramMap.pipe(switchMap(params => of(params.get('documentId'))));
    this.documentId$.subscribe(documentId => {
      if (documentId) {
        this.documentId = documentId;
        this.init();
      }
    });

    if (this.historyService.getPreviousLinkDepth() > 3) {
      this.showBackButton = true;
    }
  }

  init = async () => {
    this.userGroup = null;

    this.allPermissions = await this.ioService.post('/authorization/getPermissions');


    // Get all User Groups
    const userGroups: any = await this.ioService.post('/userGroup/getUserGroups');

    for (const userGroup of userGroups) {
      const permission = {
        name: 'Manage / Grant User Group: '+userGroup.name,
        key: 'userGroup.'+userGroup._id,
        class: 'user-group'
      };
      this.allPermissions.push(permission);
    }


    // Get all Views
    const views: any = await this.ioService.post('/view/getViews');
    
    for (const view of views) {
      let permission = {
        name: view.name+' View',
        key: 'view.'+view.key+'.view',
        class: 'view',
        locationSpecific: true
      };
      this.allPermissions.push(permission);

      permission = {
        name: view.name+' Update',
        key: 'view.'+view.key+'.update',
        class: 'view',
        locationSpecific: true
      };
      this.allPermissions.push(permission);

      if (view.creatable) {
        permission = {
          name: view.name+' Create',
          key: 'view.'+view.key+'.create',
          class: 'view',
          locationSpecific: true
        };
        this.allPermissions.push(permission);
      }

      if (view.importable) {
        permission = {
          name: view.name+' Import',
          key: 'view.'+view.key+'.import',
          class: 'view',
          locationSpecific: true
        };
        this.allPermissions.push(permission);
      }

      if (view.actions) {
        for (let action of view.actions) {
          permission = {
            name: view.name+' '+(action.name || action.key),
            key: 'view.'+view.key+'.'+action.key,
            class: 'view',
            locationSpecific: true
          };
          this.allPermissions.push(permission);
        }
      }
    }

    
    // Get All Ledgers
    const ledgers: any = await this.ioService.post('/ledger/getLedgersMinimal');

    for (const ledger of ledgers) {
      // Loop through actions object
      for (const actionKey in ledger.actions) {
        const action = ledger.actions[actionKey];
        let permission = {
          name: ledger.name+' '+action.name,
          key: 'transaction.'+ledger.key+'.'+actionKey,
          class: 'ledger',
          locationSpecific: true, //ledger.locationBased
        };
        this.allPermissions.push(permission);
      }

      // Every ledger gets a dedicated Correct permission (transaction
      // corrections), regardless of its configured actions — enforcement is
      // transaction.<ledger.key>.correct at the transaction's location.
      this.allPermissions.push({
        name: ledger.name + ' Correct',
        key: 'transaction.' + ledger.key + '.correct',
        class: 'ledger',
        locationSpecific: true,
      });

      // Tenders flagged iseCredit can settle a negative-total purchase by
      // paying the customer (ISE "Credit to Customer"). Who may do so is
      // gated per tender by purchase.credit.<ledger.key> — enforced by the
      // transaction add and mirrored in the register (manager PIN approval
      // for users without it).
      if (ledger.iseCredit) {
        this.allPermissions.push({
          name: ledger.name + ' Purchase Credit',
          key: 'purchase.credit.' + ledger.key,
          class: 'ledger',
          locationSpecific: true,
        });
      }
    }


    // Get All Purchase Types
    const purchaseTypes: any = await this.ioService.post('/transfer/getPurchaseTypes');

    for (const purchaseType of purchaseTypes) {
      let permission = {
        name: purchaseType.name+' View',
        key: 'purchase.'+purchaseType.key+'.view',
        class: 'purchase-type',
        locationSpecific: true
      };
      this.allPermissions.push(permission);

      if (Array.isArray(purchaseType.steps) && purchaseType.steps.length > 0) {
        // Stepped types: one assumed permission per step (named for the step,
        // keyed by the step key), plus the retry key for resuming movements.
        for (const step of purchaseType.steps) {
          this.allPermissions.push({
            name: purchaseType.name+' '+step.name,
            key: 'purchase.'+purchaseType.key+'.'+step.key,
            class: 'purchase-type',
            locationSpecific: true
          });
        }

        this.allPermissions.push({
          name: purchaseType.name+' Retry',
          key: 'purchase.'+purchaseType.key+'.retry',
          class: 'purchase-type',
          locationSpecific: true
        });

        // Cancel a stepped transfer (with in-transit reversal when the stock
        // has already left the source) — checked at the current step's auth
        // context by /transfer/cancelTransfer.
        this.allPermissions.push({
          name: purchaseType.name+' Cancel',
          key: 'purchase.'+purchaseType.key+'.cancel',
          class: 'purchase-type',
          locationSpecific: true
        });

        // Reverse a COMPLETED transfer (posts every inventory movement back)
        // — checked at the final step's auth context by /transfer/reverseTransfer.
        this.allPermissions.push({
          name: purchaseType.name+' Reverse',
          key: 'purchase.'+purchaseType.key+'.reverse',
          class: 'purchase-type',
          locationSpecific: true
        });

        continue;
      }

      permission = {
        name: purchaseType.name+' Update',
        key: 'purchase.'+purchaseType.key+'.update',
        class: 'purchase-type',
        locationSpecific: true
      };
      this.allPermissions.push(permission);

      permission = {
        name: purchaseType.name+' Complete',
        key: 'purchase.'+purchaseType.key+'.complete',
        class: 'purchase-type',
        locationSpecific: true
      };
      this.allPermissions.push(permission);

      // Reverse a completed legacy-flow transfer (see the stepped branch above).
      this.allPermissions.push({
        name: purchaseType.name+' Reverse',
        key: 'purchase.'+purchaseType.key+'.reverse',
        class: 'purchase-type',
        locationSpecific: true
      });
    }


    // Dashboard KPI widgets: granted like reports (widget.<id>).
    try {
      const widgets: any = await this.ioService.post('/widget/getWidgets');
      for (const widget of (widgets || [])) {
        this.allPermissions.push({
          name: 'Dashboard Widget: ' + widget.name,
          key: 'widget.' + widget._id,
          class: 'report',
          locationSpecific: true
        });
      }
    } catch (e) {
      console.log('widget permissions unavailable', e);
    }

    // Get all reports
    const reportCategories = this.reportService.getReportCategories();

    if (reportCategories) {

      for (const category of reportCategories) {
        for (const categoryReport of category.options) {
          const permission = {
            name: 'Report: '+category.name+': '+categoryReport.name,
            key: 'report.'+categoryReport._id,
            class: 'report',
            locationSpecific: true
          };
          this.allPermissions.push(permission);
        }
      }

      this.targetUserGroupAuthorizations = await this.ioService.post('/authorization/getUserGroupAuthorizations', {
        userGroupId: this.documentId
      });

      this.userGroup = await this.ioService.post('/userGroup/getUserGroup', {
        userGroupId: this.documentId
      });

      this.userGroup.managableLocations = [];

      // getLocations (unlike the active-only location cache) also returns the
      // user's authorized INACTIVE locations, with an active flag — shown
      // after the active columns so historical grants stay editable. The
      // admin-gated includeAll request returns retail AND warehouse locations:
      // warehouses carry per-location grants too (transfer steps,
      // account.inventory.detail for ISE warehouse balances).
      const gridLocations: any = await this.ioService.post('/location/getLocations', {
        type: 'retail',
        includeAll: true
      });

      for (const location of (gridLocations || [])) {
        if (this.authorizationService.checkPermission('admin', location._id)) {
          this.userGroup.managableLocations.push(location);
          this.userGroup.canManage = true;
        }
      }

      // Active locations first, then names A-Z within each group.
      this.userGroup.managableLocations.sort((a, b) => {
        const aActive = a.active === true ? 0 : 1;
        const bActive = b.active === true ? 0 : 1;
        if (aActive !== bActive) return aActive - bActive;
        return String(a.name).localeCompare(String(b.name));
      });

      this.userGroup.members = await this.ioService.post('/userGroup/getMembers', {
        userGroupId: this.documentId
      });

      this.buildSections();

      console.log('this.userGroup: ', this.userGroup);
    } else {
      setTimeout(() => {
        this.delay *= 2;
        this.init();
      }, this.delay);
    }
  }

  // Group the assembled permission rows into labeled sections. The class tag
  // set during assembly identifies each source; base Permission docs carry no
  // class and land in General Permissions. Source order is preserved within
  // each section.
  buildSections = () => {
    const sectionDefs = [
      { name: 'User Groups', match: (p) => p.class === 'user-group' },
      { name: 'General Permissions', match: (p) => !p.class },
      { name: 'View Permissions', match: (p) => p.class === 'view' },
      { name: 'Transfer Permissions', match: (p) => p.class === 'purchase-type' },
      { name: 'Ledger Actions', match: (p) => p.class === 'ledger' },
      { name: 'Reports', match: (p) => p.class === 'report' },
    ];
    this.sections = sectionDefs
      .map(def => {
        const matched = this.allPermissions.filter(def.match);
        return {
          name: def.name,
          // Global (full-width) rows lead each section, then per-location
          // rows — source order preserved within each half.
          permissions: [
            ...matched.filter(p => !p.locationSpecific),
            ...matched.filter(p => p.locationSpecific),
          ],
        };
      })
      .filter(section => section.permissions.length > 0);
  }

  isAuthorized = (locationId, permissionKey): boolean => {
    return !!(this.targetUserGroupAuthorizations[locationId]
      && this.targetUserGroupAuthorizations[locationId].indexOf(permissionKey) !== -1);
  }

  // Inactive locations render (after the active ones) and stay toggleable,
  // but the n/m badge counts ACTIVE locations only.
  activeLocationCount = (): number => {
    return this.userGroup.managableLocations.filter(location => location.active === true).length;
  }

  grantedCount = (permission): number => {
    let count = 0;
    for (const location of this.userGroup.managableLocations) {
      if (location.active === true && this.isAuthorized(location._id, permission.key)) {
        count++;
      }
    }
    return count;
  }

  setHover = (permission, col: number) => {
    this.hoverPermission = permission;
    this.hoverCol = col;
  }

  clearHover = () => {
    this.hoverPermission = null;
    this.hoverCol = -1;
  }

  toggleAuthorization = async (locationId, permissionKey) => {

    let authorized = false;
    if (this.targetUserGroupAuthorizations[locationId] && this.targetUserGroupAuthorizations[locationId].includes(permissionKey)) {
      authorized = true;
    }

    let action = 'grantToUserGroup';
    if (authorized) {
      action = 'revokeFromUserGroup';
      this.targetUserGroupAuthorizations[locationId] = this.targetUserGroupAuthorizations[locationId].filter(item => item !== permissionKey);
    } else {
      if (!this.targetUserGroupAuthorizations[locationId]) {
        this.targetUserGroupAuthorizations[locationId] = [];
      }
      this.targetUserGroupAuthorizations[locationId].push(permissionKey);
    }

    if (locationId === 'general') {
      locationId = null;
    }

    await this.ioService.post('/authorization/'+action, {
      userGroupId: this.documentId,
      key: permissionKey,
      locationId: locationId
    });

  }

  // Name-click toggle operates on ACTIVE locations only (matching the badge
  // totals); inactive columns are toggled individually.
  toggleAuthorizationAllLocations = async (permissionKey) => {

    const activeLocations = this.userGroup.managableLocations.filter(location => location.active === true);

    let locationsToAuthorize = [];
    for (const location of activeLocations) {
      if (this.targetUserGroupAuthorizations[location._id] && this.targetUserGroupAuthorizations[location._id].includes(permissionKey)) {
      } else {
        locationsToAuthorize.push(location._id);
      }
    }

    if (locationsToAuthorize.length > 0) {
      for (const locationId of locationsToAuthorize) {
        await this.ioService.post('/authorization/grantToUserGroup', {
          userGroupId: this.documentId,
          key: permissionKey,
          locationId: locationId
        });

        if (!this.targetUserGroupAuthorizations[locationId]) {
          this.targetUserGroupAuthorizations[locationId] = [];
        }

        this.targetUserGroupAuthorizations[locationId].push(permissionKey);
      }
    } else {
      for (const location of activeLocations) {
        await this.ioService.post('/authorization/revokeFromUserGroup', {
          userGroupId: this.documentId,
          key: permissionKey,
          locationId: location._id
        });
        this.targetUserGroupAuthorizations[location._id] = this.targetUserGroupAuthorizations[location._id].filter(item => item !== permissionKey);
      }
    }

  }

  saveConfigChange = async () => {
    await this.ioService.post('/userGroup/updateUserGroup', {
      _id: this.documentId,
      name: this.userGroup.name,
      default: this.userGroup.default
    });
  }

  close = () => {
    this.reportService.loadReport(null);
    this.locationR.back();
  }

}
